import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, ChevronLeft, ChevronRight, Percent, Search, Users } from 'lucide-react';
import { EmailTestModeBadge } from '../../components/revenue/EmailTestModeBadge';
import { useRevenueAccess } from '../../hooks/useRevenueAccess';

/** Rows shown in the UI table per page */
const PAGE_SIZE = 50;
/** PostgREST returns at most 1000 rows per request unless configured otherwise */
const DB_FETCH_BATCH = 1000;

/** Max length for revenue_collection_hotel_commissions.admin_comment */
const COMMISSION_COMMENT_MAX_LEN = 500;

/** Matches DB CHECK on revenue_collection_hotel_commissions.invoicing_entity */
type InvoicingEntityValue = '' | 'NL' | 'SP';

const INVOICING_ENTITY_OPTIONS: { value: InvoicingEntityValue; label: string }[] = [
  { value: '', label: 'Not set' },
  { value: 'NL', label: 'NL' },
  { value: 'SP', label: 'SP' },
];

interface HotelRow {
  id: number;
  title: string | null;
}

export default function HotelCommissions() {
  const [searchParams] = useSearchParams();
  const { canMutateRevenue } = useRevenueAccess();
  const [hotels, setHotels] = useState<HotelRow[]>([]);
  /** hotel_id -> raw commission % input string */
  const [draft, setDraft] = useState<Record<number, string>>({});
  /** hotel_id -> invoicing entity (empty string stored as NULL in DB) */
  const [draftInvoicingEntity, setDraftInvoicingEntity] = useState<
    Record<number, InvoicingEntityValue>
  >({});
  /** hotel_id -> optional short note stored on commission row */
  const [draftComment, setDraftComment] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedHint, setSavedHint] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  /** Search runs on the full hotel list (not page-limited). */
  const filteredHotels = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return hotels;
    return hotels.filter((h) => {
      const title = (h.title || '').toLowerCase();
      if (title.includes(q)) return true;
      if (String(h.id).includes(q)) return true;
      return false;
    });
  }, [hotels, searchQuery]);

  const totalFiltered = filteredHotels.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));

  const listMetaLine = useMemo(() => {
    const n = totalFiltered;
    const searched = searchQuery.trim().length > 0;
    let noun: string;
    if (searched) {
      noun = n === 1 ? 'match' : 'matches';
    } else {
      noun = n === 1 ? 'hotel' : 'hotels';
    }
    return `${n} ${noun} · ${PAGE_SIZE} per page`;
  }, [totalFiltered, searchQuery]);

  const paginatedHotels = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredHotels.slice(start, start + PAGE_SIZE);
  }, [filteredHotels, page]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list: HotelRow[] = [];
      let offset = 0;
      for (;;) {
        const { data: batch, error: hErr } = await supabase
          .from('hotels')
          .select('id, title')
          .order('title', { ascending: true })
          .range(offset, offset + DB_FETCH_BATCH - 1);
        if (hErr) throw hErr;
        const rows = (batch ?? []) as HotelRow[];
        list.push(...rows);
        if (rows.length === 0 || rows.length < DB_FETCH_BATCH) break;
        offset += DB_FETCH_BATCH;
      }

      const byHotel = new Map<number, string>();
      const byInvoicingEntity = new Map<number, InvoicingEntityValue>();
      const byComment = new Map<number, string>();
      offset = 0;
      for (;;) {
        const { data: commBatch, error: cErr } = await supabase
          .from('revenue_collection_hotel_commissions')
          .select('hotel_id, commission_percent, invoicing_entity, admin_comment')
          .order('hotel_id', { ascending: true })
          .range(offset, offset + DB_FETCH_BATCH - 1);
        if (cErr) throw cErr;
        const rows = commBatch ?? [];
        for (const r of rows as {
          hotel_id: number;
          commission_percent: string | number;
          invoicing_entity: string | null;
          admin_comment: string | null;
        }[]) {
          const n = Number(r.commission_percent);
          if (!Number.isNaN(n)) byHotel.set(r.hotel_id, String(n));
          if (r.invoicing_entity === 'NL' || r.invoicing_entity === 'SP') {
            byInvoicingEntity.set(r.hotel_id, r.invoicing_entity);
          }
          if (r.admin_comment != null && String(r.admin_comment).trim() !== '') {
            byComment.set(r.hotel_id, String(r.admin_comment));
          }
        }
        if (rows.length === 0 || rows.length < DB_FETCH_BATCH) break;
        offset += DB_FETCH_BATCH;
      }

      setHotels(list);
      const next: Record<number, string> = {};
      const nextEntity: Record<number, InvoicingEntityValue> = {};
      const nextComment: Record<number, string> = {};
      for (const h of list) {
        next[h.id] = byHotel.get(h.id) ?? '';
        nextEntity[h.id] = byInvoicingEntity.get(h.id) ?? '';
        nextComment[h.id] = byComment.get(h.id) ?? '';
      }
      setDraft(next);
      setDraftInvoicingEntity(nextEntity);
      setDraftComment(nextComment);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Deep-link from Hotel contacts: `/revenue-collection/commissions?hotelId=123` */
  useEffect(() => {
    const hid = searchParams.get('hotelId');
    if (hid != null && hid.trim() !== '') {
      setSearchQuery(hid.trim());
      setPage(1);
    }
  }, [searchParams]);

  const handleChange = (hotelId: number, value: string) => {
    const v = value.replace(',', '.');
    if (v !== '' && !/^\d*\.?\d*$/.test(v)) return;
    setDraft((prev) => ({ ...prev, [hotelId]: v }));
    setSavedHint(false);
  };

  const handleInvoicingEntityChange = (hotelId: number, value: InvoicingEntityValue) => {
    setDraftInvoicingEntity((prev) => ({ ...prev, [hotelId]: value }));
    setSavedHint(false);
  };

  const handleCommentChange = (hotelId: number, value: string) => {
    if (value.length > COMMISSION_COMMENT_MAX_LEN) return;
    setDraftComment((prev) => ({ ...prev, [hotelId]: value }));
    setSavedHint(false);
  };

  const handleSave = async () => {
    if (!canMutateRevenue) return;
    setSaving(true);
    setError(null);
    setSavedHint(false);
    try {
      const now = new Date().toISOString();
      const upserts: {
        hotel_id: number;
        commission_percent: number;
        updated_at: string;
        invoicing_entity: string | null;
        admin_comment: string | null;
      }[] = [];
      const deleteIds: number[] = [];

      for (const h of hotels) {
        const raw = (draft[h.id] ?? '').trim();
        if (raw === '') {
          deleteIds.push(h.id);
          continue;
        }
        const num = Number.parseFloat(raw);
        if (Number.isNaN(num) || num < 0 || num > 100) {
          throw new Error(`Invalid commission for "${h.title ?? h.id}" (use 0–100).`);
        }
        const ent = draftInvoicingEntity[h.id] ?? '';
        const note = (draftComment[h.id] ?? '').trim();
        upserts.push({
          hotel_id: h.id,
          commission_percent: num,
          updated_at: now,
          invoicing_entity: ent === '' ? null : ent,
          admin_comment: note === '' ? null : note,
        });
      }

      if (deleteIds.length > 0) {
        const { error: delErr } = await supabase
          .from('revenue_collection_hotel_commissions')
          .delete()
          .in('hotel_id', deleteIds);
        if (delErr) throw delErr;
      }
      if (upserts.length > 0) {
        const { error: upErr } = await supabase
          .from('revenue_collection_hotel_commissions')
          .upsert(upserts, { onConflict: 'hotel_id' });
        if (upErr) throw upErr;
      }
      setSavedHint(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-[90rem] mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <div className="flex flex-wrap items-center gap-3 min-w-0">
            <Link to="/revenue-collection" className="btn-icon-back" aria-label="Back">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <h1 className="page-title">Hotel commissions</h1>
            <EmailTestModeBadge />
          </div>
          {canMutateRevenue ? (
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || loading}
              className="btn-primary"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          ) : null}
        </div>

        <p className="text-sm text-gray-600 mb-4 max-w-3xl">
          {canMutateRevenue
            ? 'Set a commission percentage per hotel, optional short comment, and invoicing entity (NL or SP). All are stored on the same row. Leave commission blank if no default applies. Use "." as decimal separator.'
            : 'Commission percentage, comment, and invoicing entity (NL or SP) per hotel.'}
        </p>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
        )}
        {savedHint && !error && (
          <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded text-sm">
            Commission settings saved.
          </div>
        )}

        {!loading && hotels.length === 0 && (
          <div className="text-gray-500">No hotels found.</div>
        )}
        <div className={loading || hotels.length === 0 ? 'hidden' : 'space-y-3'}>
          <div className="relative max-w-md">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search all hotels by name or ID…"
              className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-action focus:outline-none focus:ring-1 focus:ring-action/30"
              aria-label="Search hotels"
            />
          </div>
          {totalFiltered > 0 && (
            <p className="text-xs text-gray-500">{listMetaLine}</p>
          )}
          <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase leading-tight">
                    Hotel
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase w-44 leading-tight">
                    <span className="inline-flex items-center gap-1">
                      <Percent className="h-3 w-3 shrink-0 text-gray-400" aria-hidden />
                      Commission (%)
                    </span>
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase min-w-[10rem] leading-tight">
                    Comment
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase w-36 leading-tight">
                    Invoicing entity
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredHotels.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-500">
                      No hotels match your search.
                    </td>
                  </tr>
                ) : (
                  paginatedHotels.map((h) => (
                    <tr key={h.id} className="align-middle">
                      <td className="px-4 py-1.5 text-gray-900 leading-tight">
                        <span className="mr-2 align-middle">{h.title || `Hotel #${h.id}`}</span>
                        <Link
                          to={`/revenue-collection/hotel-contacts?hotelId=${h.id}`}
                          className="inline-flex items-center gap-0.5 align-middle text-xs font-medium text-action hover:underline"
                          title="Invoice contacts"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Users className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                          Contacts
                        </Link>
                      </td>
                      <td className="px-4 py-1">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={draft[h.id] ?? ''}
                          onChange={(e) => handleChange(h.id, e.target.value)}
                          placeholder="—"
                          readOnly={!canMutateRevenue}
                          className="w-[7rem] border border-gray-300 rounded px-2 py-0.5 text-xs tabular-nums leading-tight read-only:bg-gray-50 read-only:text-gray-700"
                          aria-label={`Commission % for ${h.title ?? h.id}`}
                        />
                      </td>
                      <td className="px-4 py-1 min-w-[12rem] max-w-md">
                        <input
                          type="text"
                          value={draftComment[h.id] ?? ''}
                          onChange={(e) => handleCommentChange(h.id, e.target.value)}
                          maxLength={COMMISSION_COMMENT_MAX_LEN}
                          disabled={!canMutateRevenue || (draft[h.id] ?? '').trim() === ''}
                          placeholder="—"
                          title={
                            (draft[h.id] ?? '').trim() === ''
                              ? 'Set a commission % first to save a comment on this row.'
                              : `Up to ${COMMISSION_COMMENT_MAX_LEN} characters`
                          }
                          className="w-full border border-gray-300 rounded px-2 py-0.5 text-xs text-gray-900 leading-tight disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label={`Commission comment for ${h.title ?? h.id}`}
                        />
                      </td>
                      <td className="px-4 py-1">
                        <select
                          value={draftInvoicingEntity[h.id] ?? ''}
                          onChange={(e) =>
                            handleInvoicingEntityChange(h.id, e.target.value as InvoicingEntityValue)
                          }
                          disabled={!canMutateRevenue || (draft[h.id] ?? '').trim() === ''}
                          title={
                            (draft[h.id] ?? '').trim() === ''
                              ? 'Set a commission % first; invoicing entity is stored with the commission row.'
                              : undefined
                          }
                          className="w-full max-w-[10rem] border border-gray-300 rounded px-2 py-0.5 text-xs text-gray-900 bg-white leading-tight disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label={`Invoicing entity for ${h.title ?? h.id}`}
                        >
                          {INVOICING_ENTITY_OPTIONS.map((opt) => (
                            <option key={opt.value || 'not-set'} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {totalFiltered > 0 && (
              <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50/80 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-600 tabular-nums">
                  Showing{' '}
                  <span className="font-medium text-gray-900">{(page - 1) * PAGE_SIZE + 1}</span>
                  {' '}
                  <span aria-hidden>–</span>{' '}
                  <span className="font-medium text-gray-900">
                    {Math.min(page * PAGE_SIZE, totalFiltered)}
                  </span>
                  {' of '}
                  <span className="font-medium text-gray-900">{totalFiltered}</span>
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="btn-secondary-sm"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden />
                    Previous
                  </button>
                  <span className="text-sm text-gray-600 tabular-nums px-1">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="btn-secondary-sm"
                    aria-label="Next page"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {loading && <div className="text-gray-500">Loading hotels…</div>}
      </main>
    </div>
  );
}
